import { AddCircleOutline } from '@mui/icons-material'
import { Box, useTheme } from '@mui/material'
import { useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { CommonPageLayout } from 'src/components/common/CommonPageLayout'
import { IPositionDrawingProductAdd } from 'src/generated-types/position-drawing-product-add'
import { useDialogCtx } from 'src/hooks/context-hooks'
import { usePositionDrawingFilters } from 'src/hooks/usePositionDrawingFilters'
import { MUIThemeColors } from 'src/mui-theme'
import { getThemeColor } from 'src/utils'
import { invalidatePositionDrawingCache, useAddPositionDrawing } from '../../../api/position-drawing'
import { ALREADY_EXISTS } from '../../../constant'
import { ShowDialogProps } from '../../../show-dialog'
import { PositionDrawingFilters } from './components/positionDrawingFilters'
import { PositionDrawingForm } from './positionDrawingForm'
import { PositionDrawingTable } from './positionDrawingTable'

const CreatePositionDrawingDialog: React.FC<ShowDialogProps> = ({ pop }) => {
  const { t } = useTranslation(['admin-position-drawing', 'common'])

  const mutation = useAddPositionDrawing()

  useEffect(() => {
    if (mutation.error != null) {
      if (mutation.error['data'] == ALREADY_EXISTS) {
        toast(t('already-exists'), { type: 'error' })
      }
    }
  }, [mutation.error, t])

  const onSubmit = async form => {
    if (
      form.positionDrawingProducts == null ||
      form.positionDrawingProducts.length == 0 ||
      !!(form.positionDrawingProducts as IPositionDrawingProductAdd[])?.find(x => x.quantity == undefined) ||
      !!(form.positionDrawingProducts as IPositionDrawingProductAdd[])?.find(x => x.positionDrawingNumber == undefined)
    ) {
      toast(t('drawingPositionProductValidationError', { ns: 'admin-position-drawing' }), { type: 'error' })
      return
    }
    await mutation.mutateAsync(form).then(() => {
      invalidatePositionDrawingCache.useGetPositionDrawings()
      invalidatePositionDrawingCache.getPositionDrawingInMooring()
      toast.success(t('addPositionDrawingSuccess', { ns: 'admin-position-drawing' }))
      pop(true)
    })
  }

  return <PositionDrawingForm title={t('addPositionDrawing')} onSubmit={onSubmit} onCancel={() => pop()} />
}

export const PositionDrawingScreen = () => {
  const { t } = useTranslation(['admin-position-drawing', 'common'])
  const theme = useTheme()
  const { showDialog } = useDialogCtx()
  const { filters, tableFilters, onSearchChange, onProductCategoriesChange } = usePositionDrawingFilters()

  const onAdd = useCallback(async () => {
    const added = await showDialog(
      CreatePositionDrawingDialog,
      {
        opts: {
          maxWidth: 'md',
          fullWidth: true,
        },
      },
      undefined,
      true,
    )
    if (!added) return
  }, [showDialog])

  return (
    <CommonPageLayout
      titleSection={{ title: t('positionDrawing') }}
      topAction={[
        {
          buttonText: t('newDrawing'),
          buttonIcon: <AddCircleOutline />,
          onClick: onAdd,
          variant: 'outlined',
          shadowWeight: null,
          border: 1,
        },
      ]}
    >
      <PositionDrawingFilters
        filters={filters}
        onSearchChange={onSearchChange}
        onProductCategoriesChange={onProductCategoriesChange}
      />
      <Box padding="8px" bgcolor={getThemeColor(theme, MUIThemeColors.white)} borderRadius="4px">
        <PositionDrawingTable filters={tableFilters} />
      </Box>
    </CommonPageLayout>
  )
}
